import { ProbateEstatePerson } from 'lib/models/probate-estate'
import { PERSONAL_REPRESENTATIVE_ROLES_REQUIRING_CHECKS } from '../../constants'
import { UseSectionsCompleteProps } from '.'
import { PR_ROLE_VALUES } from 'utils/enums'
import {
  PROBATE_SIGNATURE_REQUEST_DOCUMENTS,
  type ProbateSignatureRequestDocument,
} from 'lib/models/probate-estate'
import {
  getActiveSignatureRequest,
  getSignatureRequestDates,
} from '../signature-request-helpers'

export type Provider = 'docusign' | 'onfido'
export type Mode = 'auto' | 'manual' | null

export const checkIsReferralComplete = ({
  values,
}: UseSectionsCompleteProps) => {
  const { referredTo } = values.quoteAttributes

  return !!referredTo
}

export const probateEstatePeopleRenouncingExecutors = (
  people: ProbateEstatePerson[]
) => {
  return people.filter(
    (person) =>
      person.attributes.personalRepresentativeRole === PR_ROLE_VALUES.renouncing
  )
}

export const probateEstatePeopleRequiringChecks = (
  people: ProbateEstatePerson[]
) => {
  return people.filter((person) => {
    if (person.attributes.isCustomer) {
      // Customer is primary executor, and we don't store `personalRepresentativeRole` for the contact that is
      // the customer.
      return true
    }

    // If we don't know the role (i.e. the user has not yet selected it) we should be cautious and assume that
    // checks for this person are required.
    if (!person.attributes.personalRepresentativeRole) {
      return true
    }

    return PERSONAL_REPRESENTATIVE_ROLES_REQUIRING_CHECKS.includes(
      person.attributes.personalRepresentativeRole
    )
  })
}

function getSignatureRequestTransitionStateDate(
  person: ProbateEstatePerson,
  targetState: 'sent' | 'signed',
  documentName: ProbateSignatureRequestDocument
): Date | null {
  const activeRequest = getActiveSignatureRequest(
    person.attributes.signatureRequests,
    documentName,
    'manual'
  )

  const dates = getSignatureRequestDates(activeRequest)
  return targetState === 'sent' ? dates.sentAt : dates.receivedAt
}

export function getSignatureRequestSentDate(
  person: ProbateEstatePerson,
  documentName: ProbateSignatureRequestDocument
): Date | null {
  return getSignatureRequestTransitionStateDate(person, 'sent', documentName)
}

export function getSignatureRequestSignedDate(
  person: ProbateEstatePerson,
  documentName: ProbateSignatureRequestDocument
): Date | null {
  return getSignatureRequestTransitionStateDate(person, 'signed', documentName)
}

export function getManualFormSentValue(
  provider: Provider,
  person: ProbateEstatePerson
) {
  switch (provider) {
    case 'docusign':
      return getSignatureRequestSentDate(
        person,
        PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS
      )
    case 'onfido':
      return person.attributes.kycChecksSentAt || null
    default:
      return null
  }
}

export function getManualFormConfirmedValue(
  provider: Provider,
  person: ProbateEstatePerson
): Date | null {
  switch (provider) {
    case 'docusign':
      return getSignatureRequestSignedDate(
        person,
        PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS
      )
    case 'onfido':
      return person.attributes.kycChecksPassedAt || null
    default:
      return null
  }
}
